import { computed, Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { SassHelpersService } from '../sass-helpers/sass-helpers.service';
import { UtilService } from '../util/util.service';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';
@Injectable({
    providedIn: 'root',
})
export class BreakPointSizeService implements OnDestroy {
    private _sassHelpers = inject(SassHelpersService);
    private _browser = inject(BrowserPlatformService);
    util = inject(UtilService);

    readonly PREFIX = '--break-';

    private _currBreakpoint = signal(Breakpoints.Mobile);
    public readonly currBreakpoint = this._currBreakpoint.asReadonly();
    public readonly isMiniMobileSize = computed(() => this._currBreakpoint() === Breakpoints.MiniMobile);
    public readonly isMobileSize = computed(() => {
        const currBreakpoint = this._currBreakpoint();
        return currBreakpoint === Breakpoints.MiniMobile || currBreakpoint === Breakpoints.Mobile;
    });
    public readonly isTabletSize = computed(() => {
        const currBreakpoint = this._currBreakpoint();
        return currBreakpoint === Breakpoints.Middle || currBreakpoint === Breakpoints.Narrow;
    });
    public readonly isDesktopSize = computed(() => {
        const currBreakpoint = this._currBreakpoint();
        return currBreakpoint === Breakpoints.Normal || currBreakpoint === Breakpoints.Wide;
    });
    public readonly isWideSize = computed(() => this._currBreakpoint() === Breakpoints.Wide);
    private resize: Subscription;
    private _subscriptions: Subscription[] = [];
    constructor() {
        this._setBreakPointSizes();
        this._setCurrBreakPointsBoolean();

        this.resize = this._browser.fromWindowEvent('resize').subscribe(() => {
            this._setCurrBreakPointsBoolean();
        });
        this._subscriptions.push(this.resize);
    }

    public breakPointSizes: { [key: string]: number } = {};

    private _setCurrBreakPointsBoolean(): void {
        const _currBreakPoint = this._getCurrBreakPoint();
        if (_currBreakPoint !== this._currBreakpoint()) {
            this._currBreakpoint.set(_currBreakPoint);
        }
    }

    private _setBreakPointSizes() {
        Object.values(Breakpoints).forEach((nameBreakPointSize: string) => {
            this.breakPointSizes[nameBreakPointSize] = this.getBreakPointSize(nameBreakPointSize);
        });
    }

    public getBreakPointSize(name: string): number {
        return +this._sassHelpers.readProperty(name, this.PREFIX).replace(/[^0-9\.]+/g, '') | 0;
    }

    private _getCurrBreakPoint(): Breakpoints {
        const innerWidth = this._browser.viewportWidth;
        if (!innerWidth) {
            return Breakpoints.Mobile;
        }
        let _currBreakPoint: Breakpoints;
        if (innerWidth < this.breakPointSizes[Breakpoints.Mobile]) {
            _currBreakPoint = Breakpoints.MiniMobile;
        } else if (innerWidth < this.breakPointSizes[Breakpoints.Middle]) {
            _currBreakPoint = Breakpoints.Mobile;
        } else if (innerWidth < this.breakPointSizes[Breakpoints.Narrow]) {
            _currBreakPoint = Breakpoints.Middle;
        } else if (innerWidth < this.breakPointSizes[Breakpoints.Normal]) {
            _currBreakPoint = Breakpoints.Narrow;
        } else if (innerWidth < this.breakPointSizes[Breakpoints.Wide]) {
            _currBreakPoint = Breakpoints.Normal;
        } else {
            _currBreakPoint = Breakpoints.Wide;
        }
        return _currBreakPoint;
    }

    ngOnDestroy(): void {
        this.util.unsubscribe(this._subscriptions);
    }
}

export enum Breakpoints {
    MiniMobile = 'mini-mobile',
    Mobile = 'mobile',
    Middle = 'middle',
    Narrow = 'narrow',
    Normal = 'normal',
    Wide = 'wide',
}
